<template>
  <div>
    <b-container fluid>
      <b-breadcrumb class="mt-3 mb-3" :items="breadcrumb_items" />

      <h3 class="mb-3">Tracking History</h3>

      <b-card class="mb-3">
        <b-form @submit.prevent="submitTracking">
          <b-row>
            <b-col md="6" class="pr-md-1">
              <b-form-input
                size="sm"
                v-model.trim="tracking_number"
                placeholder="Tracking #"
                autocomplete="off"
                autofocus
              />
            </b-col>
            <b-col>
              <b-button type="submit" class="btn-action" size="sm" variant="info">
                <font-awesome-icon class="mr-1" icon="search" />Submit
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card>

      <b-alert variant="danger" :show="!!error_message">
        {{ error_message }}
      </b-alert>

      <b-card v-if="tracking" class="mb-3">
        <b-row>
          <b-col md="2"><strong>Tracking #</strong></b-col>
          <b-col md="4">{{ tracking.tracking_number || "-" }}</b-col>
          <b-col md="2"><strong>Status</strong></b-col>
          <b-col md="4">{{ tracking.status || "-" }}</b-col>
        </b-row>
        <b-row>
          <b-col md="2"><strong>Client name</strong></b-col>
          <b-col md="4">{{ tracking.client_name || "-" }}</b-col>
          <b-col md="2"><strong>Courier name</strong></b-col>
          <b-col md="4">{{ tracking.courier_short_name || "-" }}</b-col>
        </b-row>
        <b-row>
          <b-col md="2"><strong>Orders</strong></b-col>
          <b-col md="4">{{ orderNumbers }}</b-col>
          <b-col md="2"><strong># of ctns</strong></b-col>
          <b-col md="4">{{ tracking.ctns || "-" }}</b-col>
        </b-row>
        <b-row>
          <b-col md="2"><strong>Current route</strong></b-col>
          <b-col md="4">{{ tracking.route || "-" }}</b-col>
          <b-col md="2"><strong>Scheduled delivery</strong></b-col>
          <b-col md="4">{{ tracking.scheduled_delivery_date || "-" }}</b-col>
        </b-row>
        <b-row>
          <b-col md="2"><strong>Packing sector</strong></b-col>
          <b-col md="4">{{ tracking.packing_sector || "-" }}</b-col>
        </b-row>
        <b-row>
          <b-col md="2"><strong>Created at</strong></b-col>
          <b-col md="4">{{ tracking.created_at || "-" }}</b-col>
          <b-col md="2"><strong>Last updated</strong></b-col>
          <b-col md="4">{{ tracking.updated_at || "-" }}</b-col>
        </b-row>
      </b-card>

      <div v-if="tracking">
        <h4 class="mb-3">Operational Summary</h4>
        <b-row>
          <b-col v-for="summary in operation_summary" :key="summary.type" md="6" xl="4" class="mb-3">
            <b-card class="operation-card h-100">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <strong>{{ summary.label }}</strong>
                <b-badge variant="info">{{ summary.count }}</b-badge>
              </div>
              <div v-if="summary.latest_at">
                <div><strong>Latest:</strong> {{ summary.latest_at }}</div>
                <div><strong>By:</strong> {{ summary.latest_by }}</div>
              </div>
              <div v-else class="text-muted">No record</div>
            </b-card>
          </b-col>
        </b-row>

        <h4 class="mb-3">Operation Details</h4>
        <b-card>
          <b-tabs pills card>
            <b-tab
              v-for="summary in operation_summary"
              :key="summary.type"
              :title="summary.label + ' (' + summary.count + ')'"
            >
              <b-table
                responsive
                small
                outlined
                striped
                hover
                show-empty
                :items="eventsByType(summary.type)"
                :fields="operation_fields"
                empty-text="No record for this operation."
              >
                <template v-slot:cell(created_by_name)="data">
                  {{ data.item.created_by_name || data.item.created_by || "System" }}
                </template>
                <template v-slot:cell(details)="data">
                  <span class="operation-detail">{{ data.item.details }}</span>
                </template>
              </b-table>
            </b-tab>
          </b-tabs>
        </b-card>
      </div>
    </b-container>
  </div>
</template>

<script>
const operationFields = [
  { key: "event_at", label: "Date" },
  { key: "created_by_name", label: "Performed By" },
  { key: "details", label: "Details" }
];

export default {
  name: "TrackingHistory",
  inject: ["API_HOST"],
  data() {
    return {
      tracking: null,
      tracking_number: "",
      active_tracking_number: "",
      orders: [],
      items: [],
      operation_summary: [],
      operation_fields: operationFields,
      error_message: "",
      loader: null
    };
  },
  computed: {
    orderNumbers() {
      const numbers = this.orders.map(order => order.order_number).filter(Boolean);
      return numbers.length ? numbers.join(", ") : (this.tracking && this.tracking.order_number) || "-";
    },
    breadcrumb_items() {
      return [
        { text: "Tracking History", active: true }
      ];
    }
  },
  methods: {
    submitTracking() {
      if (!this.tracking_number) {
        this.error_message = "Please enter a tracking number.";
        this.tracking = null;
        this.orders = [];
        this.items = [];
        this.operation_summary = [];
        return;
      }

      this.active_tracking_number = this.tracking_number;
      this.loadHistory();
    },
    async loadHistory() {
      this.error_message = "";
      this.loader = this.$loading.show({ isFullPage: true, canCancel: false });
      try {
        const response = await this.$http({
          method: "POST",
          baseURL: `${this.API_HOST}`,
          url: "tracking_history_get.php",
          data: {
            tracking_number: this.active_tracking_number,
            user_id: this.$store.state.user_obj.user_id,
            offset: 0,
            limit: 1000
          }
        });

        if (response.data.status !== "success") {
          throw new Error(response.data.message || "Unable to load tracking history");
        }

        this.tracking = response.data.tracking;
        this.orders = response.data.orders || [];
        this.items = response.data.operation_history || [];
        this.operation_summary = response.data.operation_summary || [];
      } catch (error) {
        this.tracking = null;
        this.orders = [];
        this.items = [];
        this.operation_summary = [];
        this.error_message = error.message || "Unable to load tracking history";
      } finally {
        if (this.loader) {
          this.loader.hide();
          this.loader = null;
        }
      }
    },
    eventsByType(type) {
      return this.items.filter(item => item.event_type === type);
    }
  }
};
</script>

<style scoped>
.operation-card {
  border-left: 4px solid #17a2b8;
}

.operation-detail {
  display: inline-block;
  min-width: 260px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>



// WEBPACK FOOTER //
// src/components/TrackingHistory.vue